import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAgentActionReport } from '../api';
import { statusFilterToReportParams } from '../filterActions';
import { reportCacheKey } from '../reportUtils';
import type { StatusFilter, TimeRange } from '../types';

export interface CachedReport {
  report: string;
  generated_at: string;
  actions_analyzed: number;
  cached_at: number;
}

const CACHE_MS = 5 * 60 * 1000;

function parseReportError(e: unknown): string {
  if (!(e instanceof Error)) return 'Report generation failed';
  const jsonMatch = e.message.match(/\{[\s\S]*\}$/);
  if (jsonMatch) {
    try {
      const body = JSON.parse(jsonMatch[0]) as { details?: string; error?: string };
      if (body.details) return body.details;
      if (body.error) return body.error.replace(/_/g, ' ');
    } catch {
      /* ignore */
    }
  }
  return e.message;
}

function sinceFromRange(range: TimeRange): string {
  const hours = range === '7d' ? 24 * 7 : range === '30d' ? 24 * 30 : 24;
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

export function useAgentActionReport(
  appId: string | null,
  range: TimeRange,
  statusFilter: StatusFilter,
) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CachedReport | null>(null);

  const reportParams = useMemo(() => statusFilterToReportParams(statusFilter), [statusFilter]);
  const storageKey = useMemo(
    () => reportCacheKey(appId, range, reportParams.focus, statusFilter),
    [appId, range, reportParams.focus, statusFilter],
  );

  useEffect(() => {
    setResult(null);
    setError(null);
    setOpen(false);
  }, [storageKey]);

  const generate = useCallback(async (regenerate = false) => {
    setOpen(true);
    setLoading(true);
    setError(null);

    if (!regenerate) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as CachedReport;
          if (Date.now() - parsed.cached_at < CACHE_MS) {
            setResult(parsed);
            setLoading(false);
            return;
          }
        }
      } catch {
        /* ignore */
      }
    }

    try {
      const resp = await fetchAgentActionReport({
        app_id: appId,
        since: sinceFromRange(range),
        until: new Date().toISOString(),
        focus: reportParams.focus,
        status_filter: reportParams.status_filter,
        regenerate,
      });
      const cached: CachedReport = {
        report: resp.report,
        generated_at: resp.generated_at,
        actions_analyzed: resp.actions_analyzed,
        cached_at: Date.now(),
      };
      localStorage.setItem(storageKey, JSON.stringify(cached));
      setResult(cached);
    } catch (e) {
      setError(parseReportError(e));
    } finally {
      setLoading(false);
    }
  }, [appId, range, reportParams, storageKey]);

  const close = useCallback(() => setOpen(false), []);

  return { open, loading, error, result, generate, close };
}
